import {
  useQuery,
  useMutation,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Certification,
  SingleCertificationResponse,
} from "../Models/certification";
import {
  getCertCategories,
  getUserCategories,
  createCertCategory,
  editCertCategory,
  deleteCertCategory,
  getSingleCertCategory,
} from "../Api/certifications";
import { UserRole } from "../Enums/companyStatus";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { hasRole } from "../Helpers/authHelpers";

export const useCertifications = () => {
  return useQuery<Certification[], Error>({
    queryKey: ["certifications"],
    queryFn: () =>
      hasRole(UserRole.ADMIN) ? getCertCategories() : getUserCategories(),
    placeholderData: keepPreviousData,
  });
};

export const useSingleCertification = (id: number | undefined) => {
  return useQuery<SingleCertificationResponse, AxiosError>({
    queryKey: ["certification", id],
    queryFn: () => getSingleCertCategory(id!),
    enabled: !!id,
  });
};

export const useCreateCertification = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newCategory: Omit<Certification, "id">) =>
      createCertCategory(newCategory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certifications"] });
      navigate("/question_list/");
    },
  });
};

export const useUpdateCertification = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updatedCategory,
    }: {
      id: number;
      updatedCategory: Omit<Certification, "id">;
    }) => editCertCategory(id, updatedCategory),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["certifications"] });
      queryClient.invalidateQueries({
        queryKey: ["certification", variables.id],
      });
      navigate("/cert_category/");
    },
  });
};

export const useDeleteCertification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCertCategory(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["certifications"] });
      queryClient.invalidateQueries({
        queryKey: ["certification", id],
      });
    },
  });
};
